import Parse from '../../../config/parse';
import { notifyNewQuotation, notifyNewUser, updateQuotationStatusWithNotification } from '../../../services/email/quotationEmailService';
import { checkMagicLinkStatus, generateMagicLinkToken } from './tokenService';

/**
 * Servicio de autenticación para cotizaciones
 * 
 * Este servicio maneja la autenticación y acceso a cotizaciones mediante magic links,
 * permitiendo a usuarios no autenticados acceder a sus cotizaciones y convertirlas en cuentas.
 */

/**
 * Convierte una cotización de magic link a una cuenta de usuario
 * @param {string} magicLink - El magic link de la cotización
 * @param {Object} userData - Datos del usuario para crear la cuenta
 * @returns {Promise<Object>} - Objeto con el usuario y la cotización
 */
export const convertQuotationToAccount = async (magicLink, userData) => {
  try {
    console.log('Iniciando conversión de cotización a cuenta:', {
      magicLink: magicLink ? 'Proporcionado' : 'No proporcionado',
      email: userData.email,
      name: userData.name
    });

    // 1. Buscar la cotización por magic link
    const Quotation = Parse.Object.extend('Quotation');
    const query = new Parse.Query(Quotation);
    query.equalTo('magicLink', magicLink);
    const quotation = await query.first();

    if (!quotation) {
      console.error('Cotización no encontrada para conversión:', magicLink);
      throw new Error('Cotización no encontrada');
    }

    console.log('Cotización encontrada para conversión:', {
      id: quotation.id,
      userEmail: quotation.get('userEmail'),
      status: quotation.get('status')
    });

    // 2. Crear o encontrar el usuario
    let user;
    let isNewUser = false;
    try {
      // Intentar login si el usuario existe
      console.log('Intentando login con usuario existente:', userData.email);
      user = await Parse.User.logIn(userData.email, userData.password);
      console.log('Login exitoso, usuario existente');
    } catch (error) {
      // Si no existe, crear nuevo usuario
      console.log('Usuario no existe, creando nueva cuenta:', userData.email);
      user = new Parse.User();
      user.set('username', userData.email);
      user.set('email', userData.email);
      user.set('password', userData.password);
      user.set('name', userData.name || quotation.get('userName'));
      user.set('phone', userData.phone || quotation.get('userPhone'));
      user.set('type', 'client'); // Asegurar que sea cliente
      user = await user.signUp();
      isNewUser = true;
      console.log('Nuevo usuario creado exitosamente');
    }

    // 3. Asociar la cotización con el usuario
    quotation.set('user', user);
    quotation.set('converted', true);
    await quotation.save();
    console.log('Cotización asociada exitosamente al usuario');
    
    // 4. Enviar email de bienvenida al usuario (solo si es nuevo)
    if (isNewUser) {
      try {
        console.log('Enviando email de bienvenida al nuevo usuario');
        await notifyNewUser(user);
        console.log('Email de bienvenida enviado exitosamente');
      } catch (emailError) {
        console.warn('No se pudo enviar el email de bienvenida:', emailError);
        // Continuamos con el flujo aunque falle el envío del email
      }
    }

    return {
      user,
      quotation,
      isNewUser
    };
  } catch (error) {
    console.error('Error al convertir cotización a cuenta:', error);
    throw new Error(`Error al convertir cotización: ${error.message}`);
  }
};

// Obtener todas las cotizaciones de un usuario
export const getUserQuotations = async (user) => {
  try {
    const Quotation = Parse.Object.extend('Quotation');
    const query = new Parse.Query(Quotation);
    
    // Verificar si el usuario es administrador
    const isAdmin = user.get('role') === 'admin' || user.get('isAdmin') === true;
    
    // Si no es admin, filtrar solo por las cotizaciones del usuario
    if (!isAdmin) {
      query.equalTo('user', user);
    }
    
    query.descending('createdAt');
    return await query.find();
  } catch (error) {
    console.error('Error en getUserQuotations:', error);
    throw new Error(`Error al obtener cotizaciones: ${error.message}`);
  }
};

/**
 * Obtiene una cotización específica por su magic link
 * @param {string} magicLink - El magic link único de la cotización
 * @returns {Promise<Object>} - La cotización encontrada
 */
export const getQuotationByMagicLink = async (magicLink) => {
  try {
    console.log('Buscando cotización con magic link:', magicLink);
    
    if (!magicLink) {
      console.error('Magic link no proporcionado');
      throw new Error('Magic link no válido');
    }

    const Quotation = Parse.Object.extend('Quotation');
    const query = new Parse.Query(Quotation);
    query.equalTo('magicLink', magicLink);
    const quotation = await query.first();

    if (!quotation) {
      console.error('Cotización no encontrada para el magic link:', magicLink);
      throw new Error('Cotización no encontrada');
    }

    console.log('Cotización encontrada:', {
      id: quotation.id,
      createdAt: quotation.get('createdAt'),
      status: quotation.get('status'),
      userEmail: quotation.get('userEmail')
    });

    return quotation;
  } catch (error) {
    console.error('Error al obtener cotización por magic link:', error);
    throw new Error(`Error al obtener cotización: ${error.message}`);
  }
};

/**
 * Verifica si un magic link es válido y devuelve información sobre la cotización
 * @param {string} magicLink - El magic link a verificar
 * @returns {Promise<Object>} - Objeto con información sobre la validez del magic link
 */
export const verifyMagicLink = async (magicLink) => {
  try {
    console.log('Verificando validez del magic link:', magicLink);
    
    if (!magicLink) {
      console.error('Magic link no proporcionado para verificación');
      return {
        isValid: false,
        quotation: null,
        isConverted: false,
        error: 'Magic link no proporcionado',
        isExpired: true
      };
    }

    // Verificar si el token ha expirado
    const magicLinkStatus = checkMagicLinkStatus(magicLink);
    if (magicLinkStatus.isExpired) {
      console.warn('Magic link expirado:', magicLink);
      return {
        isValid: false,
        quotation: null,
        isConverted: false,
        error: 'El enlace ha expirado',
        isExpired: true,
        expiresAt: magicLinkStatus.expiresAt
      };
    }

    const Quotation = Parse.Object.extend('Quotation');
    const query = new Parse.Query(Quotation);
    query.equalTo('magicLink', magicLink);
    const quotation = await query.first();
    
    const isValid = !!quotation;
    const isConverted = quotation ? !!quotation.get('converted') : false;
    
    console.log('Resultado de verificación de magic link:', {
      magicLink,
      isValid,
      quotationId: quotation ? quotation.id : null,
      isConverted,
      isExpired: magicLinkStatus.isExpired,
      remainingTime: Math.floor(magicLinkStatus.remainingTime / (1000 * 60 * 60)) + ' horas'
    });
    
    return {
      isValid,
      quotation,
      isConverted,
      status: quotation ? quotation.get('status') : null,
      isExpired: magicLinkStatus.isExpired,
      expiresAt: magicLinkStatus.expiresAt,
      remainingTime: magicLinkStatus.remainingTime
    };
  } catch (error) {
    console.error('Error al verificar magic link:', error);
    return {
      isValid: false,
      quotation: null,
      isConverted: false,
      error: error.message,
      isExpired: true
    };
  }
};

/**
 * Genera un nuevo magic link para una cotización
 * @param {Object} quotation - La cotización para la que generar el magic link
 * @returns {Promise<string>} - El nuevo magic link generado
 */
export const regenerateMagicLink = async (quotation) => {
  try {
    if (!quotation) {
      throw new Error('Cotización no proporcionada');
    }
    
    // Crear un objeto con los datos mínimos necesarios para el token
    const userData = {
      id: quotation.id,
      get: (field) => {
        switch(field) {
          case 'email': return quotation.get('userEmail');
          case 'userType': return 'client';
          default: return null;
        }
      }
    };
    
    // Generar un nuevo magic link token
    const newMagicLink = generateMagicLinkToken(userData);
    
    // Actualizar la cotización con el nuevo magic link
    quotation.set('magicLink', newMagicLink);
    await quotation.save(null, { useMasterKey: true });
    
    console.log('Magic link regenerado exitosamente para cotización:', quotation.id);
    return newMagicLink;
  } catch (error) {
    console.error('Error al regenerar magic link:', error);
    throw new Error(`Error al regenerar magic link: ${error.message}`);
  }
};

// Verificar si existe un email
export const checkEmailExists = async (email) => {
  if (!email) {
    console.error('Email no proporcionado para verificación');
    return false;
  }

  try {
    // Crear una consulta compuesta para buscar por username o email
    const usernameQuery = new Parse.Query(Parse.User);
    usernameQuery.equalTo('username', email);
    
    const emailQuery = new Parse.Query(Parse.User);
    emailQuery.equalTo('email', email);
    
    // Combinar las consultas con OR
    const query = Parse.Query.or(usernameQuery, emailQuery);
    
    // Ejecutar la consulta con masterKey para evitar problemas de permisos
    const user = await query.first({ useMasterKey: true });
    
    // Si encontramos un usuario, devolver true
    if (user) {
      console.log(`Usuario encontrado para el email: ${email}`);
      return true;
    }
    
    // Si no encontramos usuario, intentar con el método de login como fallback
    try {
      await Parse.User.logIn(email, 'dummy-password');
      // Si llegamos aquí sin error, algo raro pasó
      console.log(`Login exitoso con dummy-password para: ${email}`);
      return true;
    } catch (loginError) {
      // Si el error es de contraseña incorrecta (101), significa que el usuario existe
      if (loginError.code === 101) {
        console.log(`Usuario existe pero contraseña incorrecta para: ${email}`);
        return true;
      }
      
      // Si el error es de usuario no encontrado (101, 205, 206), el usuario no existe
      if ([101, 205, 206].includes(loginError.code)) {
        console.log(`Usuario no encontrado para el email: ${email}`);
        return false;
      }
      
      // Para cualquier otro error, asumimos que el usuario no existe
      console.log(`Error desconocido al verificar email: ${email}, código: ${loginError.code}`);
      return false;
    }
  } catch (error) {
    console.error(`Error al verificar si existe el email ${email}:`, error);
    // En caso de error, asumimos que el usuario no existe para evitar problemas
    return false;
  }
};
